/**
 * Hook for managing portfolio actions (pinning operations and batch operations)
 */

import { getTierColors } from '../../services/TierColorService';

/**
 * Custom hook for portfolio action functions
 * @param {Array} pinnedClients - Current pinned clients array
 * @param {Array} orderedPinnedClients - Current ordered pinned clients array
 * @param {Function} setPinnedClients - Function to update pinned clients
 * @param {Function} setOrderedPinnedClients - Function to update ordered pinned clients
 * @param {boolean} isHardPinned - Hard pin state
 * @returns {Object} Portfolio action functions
 */
const usePortfolioActions = (pinnedClients, orderedPinnedClients, setPinnedClients, setOrderedPinnedClients, isHardPinned) => {
  
  // Pin all tier clients at once (batch operation)
  const pinTierClients = (tierClientsArray) => {
    if (!tierClientsArray || tierClientsArray.length === 0) return;
    
    // Keep existing pins and add new ones
    const existingPins = [...pinnedClients];
    const existingOrderedPins = [...orderedPinnedClients];
    
    const newPinnedClients = [...existingPins];
    const newOrderedPinnedClients = [...existingOrderedPins];
    
    tierClientsArray.forEach(client => {
      // Skip if client is already pinned
      if (existingPins.some(pinned => 
        pinned.client_name === client.client_name && pinned.country === client.country
      )) {
        return;
      }
      
      // Get tier-based colors
      const tierColors = getTierColors();
      const clientTier = client['Tier'] || 'Unknown';
      const tierColorData = tierColors[clientTier] || tierColors['Unknown'];
      
      const clientWithColor = {
        ...client,
        cardColor: tierColorData.light,
        cardAccent: tierColorData.dark
      };
      
      newPinnedClients.push(clientWithColor);
      newOrderedPinnedClients.push(clientWithColor);
    });
    
    // Update state once with all new clients
    setPinnedClients(newPinnedClients);
    setOrderedPinnedClients(newOrderedPinnedClients);
  };

  // Pin all clients in a country at once (batch operation)
  const pinCountryClients = (countryName, clientData) => {
    if (!countryName || !clientData || clientData.length === 0) return;
    
    // Find all clients for this country
    const countryClients = clientData.filter(client => 
      client.country === countryName
    );
    
    if (countryClients.length === 0) return;
    
    // Keep existing pins and add new ones
    const existingPins = [...pinnedClients];
    const existingOrderedPins = [...orderedPinnedClients];
    
    const newPinnedClients = [...existingPins];
    const newOrderedPinnedClients = [...existingOrderedPins];
    
    let addedCount = 0;
    
    countryClients.forEach(client => {
      // Skip if client is already pinned
      if (existingPins.some(pinned => 
        pinned.client_name === client.client_name && pinned.country === client.country
      )) {
        return;
      }
      
      // Get tier-based colors
      const tierColors = getTierColors();
      const clientTier = client['Tier'] || 'Unknown';
      const tierColorData = tierColors[clientTier] || tierColors['Unknown'];
      
      const clientWithColor = {
        ...client,
        cardColor: tierColorData.light,
        cardAccent: tierColorData.dark
      };
      
      newPinnedClients.push(clientWithColor);
      newOrderedPinnedClients.push(clientWithColor);
      addedCount++;
    });
    
    // Update state once with all new clients
    if (addedCount > 0) {
      setPinnedClients(newPinnedClients);
      setOrderedPinnedClients(newOrderedPinnedClients);
    }
    
    return addedCount; // Return number of clients added for UI feedback
  };

  // Unpin all clients from a specific country
  const unpinCountryClients = (countryName) => {
    if (!countryName) return;
    
    // Filter out all clients from this country
    const newPinnedClients = pinnedClients.filter(client => 
      client.country !== countryName
    );
    const newOrderedPinnedClients = orderedPinnedClients.filter(client => 
      client.country !== countryName
    );
    
    const removedCount = pinnedClients.length - newPinnedClients.length;
    
    // Update state
    setPinnedClients(newPinnedClients);
    setOrderedPinnedClients(newOrderedPinnedClients);
    
    return removedCount;
  };

  return {
    pinTierClients,
    pinCountryClients,
    unpinCountryClients
  };
};

export default usePortfolioActions;
